/**
 * AI プロンプト管理サービス
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './config';
import {
  AIPrompt,
  AIPromptInput,
  AIPromptUpdate,
  AIPromptHistory,
  DEFAULT_PROMPT_WEIGHTS,
  DEFAULT_INBREEDING_THRESHOLDS,
  validateWeightsTotal,
  validateThresholdsOrder,
} from '@/types/aiManagement';

/**
 * AIプロンプト管理サービスクラス
 */
export class AIPromptService {
  private readonly PROMPTS_COLLECTION = 'aiPrompts';
  private readonly HISTORY_COLLECTION = 'aiPromptHistory';

  /**
   * ユーザーのプロンプト一覧を取得
   */
  async getPrompts(userId: string, activeOnly = false): Promise<AIPrompt[]> {
    try {
      const constraints = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
      ];

      if (activeOnly) {
        constraints.splice(1, 0, where('isActive', '==', true));
      }

      const q = query(collection(db, this.PROMPTS_COLLECTION), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIPrompt[];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプト一覧の取得に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプト一覧の取得に失敗しました');
    }
  }

  /**
   * プロンプトをIDで取得
   */
  async getPromptById(id: string): Promise<AIPrompt | null> {
    try {
      const docRef = doc(db, this.PROMPTS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() } as AIPrompt;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプトの取得に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプトの取得に失敗しました');
    }
  }

  /**
   * ユーザーのデフォルトプロンプトを取得
   */
  async getDefaultPrompt(userId: string): Promise<AIPrompt | null> {
    try {
      const q = query(
        collection(db, this.PROMPTS_COLLECTION),
        where('userId', '==', userId),
        where('isDefault', '==', true),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const docData = snapshot.docs[0];
      return { id: docData.id, ...docData.data() } as AIPrompt;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`デフォルトプロンプトの取得に失敗しました: ${error.message}`);
      }
      throw new Error('デフォルトプロンプトの取得に失敗しました');
    }
  }

  /**
   * 新しいプロンプトを作成
   */
  async createPrompt(data: AIPromptInput): Promise<string> {
    try {
      // 重み付け検証
      if (!validateWeightsTotal(data.weights)) {
        throw new Error('評価基準の重み付けの合計は100%である必要があります');
      }

      // 閾値検証
      if (!validateThresholdsOrder(data.inbreedingThresholds)) {
        throw new Error('近親係数の閾値は safe < caution < warning の順序である必要があります');
      }

      const docRef = doc(collection(db, this.PROMPTS_COLLECTION));
      const now = Timestamp.now();

      const prompt: Omit<AIPrompt, 'id'> = {
        ...data,
        version: 1,
        createdAt: now,
        updatedAt: now,
      };

      // デフォルトに設定する場合、他のデフォルトを解除
      if (data.isDefault) {
        await this.unsetOtherDefaults(data.userId, null);
      }

      await setDoc(docRef, prompt);

      return docRef.id;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプトの作成に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプトの作成に失敗しました');
    }
  }

  /**
   * プロンプトを更新（履歴も自動作成）
   */
  async updatePrompt(
    id: string,
    data: AIPromptUpdate,
    changeNote?: string
  ): Promise<void> {
    try {
      // 現在のプロンプトを取得
      const currentPrompt = await this.getPromptById(id);
      if (!currentPrompt) {
        throw new Error('プロンプトが見つかりません');
      }

      // 重み付け検証
      if (data.weights && !validateWeightsTotal(data.weights)) {
        throw new Error('評価基準の重み付けの合計は100%である必要があります');
      }

      // 閾値検証
      if (data.inbreedingThresholds && !validateThresholdsOrder(data.inbreedingThresholds)) {
        throw new Error('近親係数の閾値は safe < caution < warning の順序である必要があります');
      }

      const batch = writeBatch(db);
      const now = Timestamp.now();

      // 履歴を作成
      const historyRef = doc(collection(db, this.HISTORY_COLLECTION));
      const history: Omit<AIPromptHistory, 'id'> = {
        promptId: id,
        userId: currentPrompt.userId,
        version: currentPrompt.version,
        snapshot: currentPrompt,
        changedBy: currentPrompt.userId,
        changeNote,
        createdAt: now,
      };
      batch.set(historyRef, history);

      // プロンプトを更新
      const promptRef = doc(db, this.PROMPTS_COLLECTION, id);
      const updateData = {
        ...data,
        version: currentPrompt.version + 1,
        updatedAt: now,
      };

      // デフォルトに設定する場合、他のデフォルトを解除
      if (data.isDefault && !currentPrompt.isDefault) {
        await this.unsetOtherDefaults(currentPrompt.userId, id);
      }

      batch.update(promptRef, updateData);

      await batch.commit();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプトの更新に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプトの更新に失敗しました');
    }
  }

  /**
   * プロンプトを削除（論理削除）
   */
  async deletePrompt(id: string): Promise<void> {
    try {
      const prompt = await this.getPromptById(id);
      if (!prompt) {
        throw new Error('プロンプトが見つかりません');
      }

      if (prompt.isDefault) {
        throw new Error('デフォルトプロンプトは削除できません。先に他のプロンプトをデフォルトに設定してください。');
      }

      const docRef = doc(db, this.PROMPTS_COLLECTION, id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプトの削除に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプトの削除に失敗しました');
    }
  }

  /**
   * プロンプトを複製
   */
  async duplicatePrompt(id: string, newName: string): Promise<string> {
    try {
      const original = await this.getPromptById(id);
      if (!original) {
        throw new Error('複製元のプロンプトが見つかりません');
      }

      const duplicateData: AIPromptInput = {
        userId: original.userId,
        name: newName,
        description: original.description,
        version: 1, // リセット
        isActive: true,
        isDefault: false, // デフォルトフラグはコピーしない
        weights: { ...original.weights },
        inbreedingThresholds: { ...original.inbreedingThresholds },
        customInstructions: original.customInstructions,
      };

      return await this.createPrompt(duplicateData);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`プロンプトの複製に失敗しました: ${error.message}`);
      }
      throw new Error('プロンプトの複製に失敗しました');
    }
  }

  /**
   * プロンプトのバージョン履歴を取得
   */
  async getPromptHistory(promptId: string, userId: string): Promise<AIPromptHistory[]> {
    try {
      const q = query(
        collection(db, this.HISTORY_COLLECTION),
        where('promptId', '==', promptId),
        where('userId', '==', userId),
        orderBy('version', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIPromptHistory[];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`バージョン履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('バージョン履歴の取得に失敗しました');
    }
  }

  /**
   * デフォルトプロンプトを作成（初回ユーザー向け）
   */
  async createDefaultPromptIfNotExists(userId: string): Promise<AIPrompt | null> {
    try {
      const existingDefault = await this.getDefaultPrompt(userId);
      if (existingDefault) {
        return existingDefault;
      }

      const defaultData: AIPromptInput = {
        userId,
        name: '標準交配提案',
        description: 'デフォルトの交配提案プロンプトです。重み付けと閾値を調整できます。',
        version: 1,
        isActive: true,
        isDefault: true,
        weights: DEFAULT_PROMPT_WEIGHTS,
        inbreedingThresholds: DEFAULT_INBREEDING_THRESHOLDS,
      };

      const id = await this.createPrompt(defaultData);
      return await this.getPromptById(id);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`デフォルトプロンプトの作成に失敗しました: ${error.message}`);
      }
      throw new Error('デフォルトプロンプトの作成に失敗しました');
    }
  }

  /**
   * 他のプロンプトのデフォルトフラグを解除
   */
  private async unsetOtherDefaults(userId: string, excludeId: string | null): Promise<void> {
    const q = query(
      collection(db, this.PROMPTS_COLLECTION),
      where('userId', '==', userId),
      where('isDefault', '==', true)
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      if (docSnap.id !== excludeId) {
        batch.update(doc(db, this.PROMPTS_COLLECTION, docSnap.id), {
          isDefault: false,
          updatedAt: Timestamp.now(),
        });
      }
    });

    await batch.commit();
  }
}

// シングルトンインスタンス
export const aiPromptService = new AIPromptService();
